import { useEffect, useMemo, useState } from "react";
import { getPedidosPendientesDireccion } from "../services/pedidosService";
import { getClientePorId } from "../services/clientesService";
import { getDireccionesPorCliente } from "../services/direccionesService";

// Campos de la dirección que se toman de /envasado_direcciones_entrega/cli
// tras editarla. Se eligen explícitamente para no pisar datos del cliente.
const CAMPOS_DIRECCION = [
  "direccion_entrega",
  "nombre_direccion",
  "contacto",
  "telefono_1",
  "telefono_2",
  "telefono_3",
  "mails",
  "latitud",
  "longitud",
  "observaciones",
  "id_localidad",
  "localidad",
  "id_provincia",
  "provincia",
  "id_sucursal",
  "zona",
];

const extraerCamposDireccion = (direccion) =>
  CAMPOS_DIRECCION.reduce((acc, campo) => {
    acc[campo] = direccion[campo];
    return acc;
  }, {});

// Pasa los datos de /envasado_clientes/xid a los nombres de campo que trae
// buscarDireccionesSucAuth.
const mapearClienteADireccion = (cliente) => ({
  razon_social: cliente.razon_social,
  sisven_id: cliente.sisven_id,
  cuit: cliente.cuit,
  factura_consfinal: cliente.factura_consfinal,
  nombre_sucursal: cliente.sucursal,
  cliente_telefono_1: cliente.telefono_1,
  cliente_telefono_2: cliente.telefono_2,
  cliente_telefono_3: cliente.telefono_3,
  mails_cliente: cliente.mails,
  obs_cliente: cliente.observaciones,
  cond_iva: cliente.nombre_situacioniva,
});

// Dirección elegida, cliente asociado, aviso de pedido pendiente y detección
// de coordenadas faltantes. "tabla" se recibe porque el original re-evalúa
// las coordenadas al volver de los sub-modales.
export const useSeleccionDireccion = ({ tabla }) => {
  const [direccionSeleccionada, setdireccionSeleccionada] = useState("");
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [pedidoPendDirec, setPedidoPendDirec] = useState();
  const [agregarCoordenadas, setAgregarCoordenadas] = useState(false);
  // Datos del cliente recargados tras editarlo. Se guardan aparte para no
  // cambiar direccionSeleccionada/clienteSeleccionado, que reinician los renglones.
  const [clienteActualizado, setClienteActualizado] = useState(null);
  // Ídem para la dirección recargada tras editarla.
  const [direccionActualizada, setDireccionActualizada] = useState(null);

  const handleDireccionSeleccionada = (newValue) => {
    setClienteActualizado(null);
    setDireccionActualizada(null);

    if (!newValue) {
      setdireccionSeleccionada("");
      setClienteSeleccionado("");
      return;
    }

    setdireccionSeleccionada(newValue);

    if (newValue.id_cliente) {
      setClienteSeleccionado({
        id: newValue.id_cliente,
        razon_social: newValue.razon_social,
        sisven_id: newValue.sisven_id,
        sucursal: newValue.cliente_sucursal || newValue.sucursal,
      });
    }
  };

  const limpiarSeleccion = () => {
    setdireccionSeleccionada("");
    setClienteSeleccionado("");
    setClienteActualizado(null);
    setDireccionActualizada(null);
  };

  // Trae la dirección seleccionada con la forma de la ficha de cliente, para
  // pasársela a EdicionDireccionEnv. Devuelve undefined si no la encuentra.
  const obtenerDireccionParaEdicion = async () => {
    if (!direccionSeleccionada || !direccionSeleccionada.id_cliente) return undefined;

    const direcciones = await getDireccionesPorCliente(direccionSeleccionada.id_cliente);
    return direcciones.find((d) => d.id === direccionSeleccionada.id);
  };

  // Lo llama EdicionDireccionEnv (prop "actualizar") después de guardar.
  const refrescarDireccion = () => {
    obtenerDireccionParaEdicion()
      .then((direccion) => {
        if (direccion) setDireccionActualizada(extraerCamposDireccion(direccion));
      })
      .catch((error) => console.error("Error al refrescar la dirección:", error));
  };

  // Lo llama EditarClientePedido (prop "actualizar") al cerrar su aviso.
  const refrescarCliente = () => {
    if (!clienteSeleccionado || !clienteSeleccionado.id) return;

    getClientePorId(clienteSeleccionado.id)
      .then((cliente) => {
        if (cliente) setClienteActualizado(mapearClienteADireccion(cliente));
      })
      .catch((error) => console.error("Error al refrescar el cliente:", error));
  };

  // Versiones para mostrar: la selección original con los datos recargados encima.
  const direccionDetalle = useMemo(
    () =>
      direccionSeleccionada && (clienteActualizado || direccionActualizada)
        ? {
            ...direccionSeleccionada,
            ...(direccionActualizada || {}),
            ...(clienteActualizado || {}),
          }
        : direccionSeleccionada,
    [direccionSeleccionada, clienteActualizado, direccionActualizada],
  );

  const clienteDetalle = useMemo(
    () =>
      clienteSeleccionado && clienteActualizado
        ? {
            ...clienteSeleccionado,
            razon_social: clienteActualizado.razon_social,
            sisven_id: clienteActualizado.sisven_id,
          }
        : clienteSeleccionado,
    [clienteSeleccionado, clienteActualizado],
  );

  useEffect(() => {
    if (direccionSeleccionada && direccionSeleccionada.id) {
      getPedidosPendientesDireccion(direccionSeleccionada.id).then(setPedidoPendDirec);
    }
  }, [direccionSeleccionada]);

  // Se evalúa sobre direccionDetalle para reflejar coordenadas cargadas al
  // editar la dirección.
  useEffect(() => {
    if (
      direccionDetalle &&
      (direccionDetalle.latitud === null || direccionDetalle.longitud === null)
    ) {
      setAgregarCoordenadas(true);
    } else {
      setAgregarCoordenadas(false);
    }
  }, [direccionDetalle, tabla]);

  return {
    direccionSeleccionada,
    clienteSeleccionado,
    pedidoPendDirec,
    agregarCoordenadas,
    handleDireccionSeleccionada,
    limpiarSeleccion,
    refrescarCliente,
    obtenerDireccionParaEdicion,
    refrescarDireccion,
    direccionDetalle,
    clienteDetalle,
  };
};
